import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import Input from '@/components/forms/Input/Input'
import PasswordEyeInput from '@/components/forms/PasswordEyeInput/PasswordEyeInput'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type LoginRequest, type SignupRequest, type AuthResult } from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './Auth.css'
import '@/styles/Auth.css'
import '@/styles/General.css'

type AuthStep = 'select-type' | 'login' | 'signup'
type UserType = 'provider' | 'receiver'

const Auth = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { logIn, registerAccount, isLoading, isLogged, getDefaultRoute } = useAuthStore()
    const { t } = useTranslation('common')

    const [currentStep, setCurrentStep] = useState<AuthStep>('select-type')
    const [selectedUserType, setSelectedUserType] = useState<UserType>('receiver')
    const [hasBeenSlidDown, setHasBeenSlidDown] = useState(false)
    const [formError, setFormError] = useState<string>('')
    const [isFormActive, setIsFormActive] = useState(false)

    const [loginData, setLoginData] = useState<LoginRequest>({
        email: '',
        password: '',
    })

    const [signupData, setSignupData] = useState<SignupRequest>({
        email: '',
        password1: '',
        password2: '',
        user_type: 'receiver',
    })

    // Password validation state
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasNumber: false,
        hasLetter: false,
        notNumeric: true,
        match: false
    })

    // Handle query params for step navigation
    useEffect(() => {
        const step = searchParams.get('step')
        const type = searchParams.get('type')
        
        if (step === 'login') {
            setCurrentStep('login')
        } else if (step === 'signup') {
            if (type === 'provider' || type === 'receiver') {
                setSelectedUserType(type as UserType)
                setSignupData(prev => ({ ...prev, user_type: type as UserType }))
            }
            setHasBeenSlidDown(true)
            setCurrentStep('signup')
        }
    }, [searchParams])

    useEffect(() => {
        if (isLogged) {
            // Get user-specific route based on account type
            getDefaultRoute().then(route => {
                navigate(route)
            })
        }
    }, [isLogged, navigate, getDefaultRoute])

    // Real-time password validation
    useEffect(() => {
        const { password1, password2 } = signupData

        setPasswordRequirements({
            minLength: password1.length >= 8,
            hasNumber: /\d/.test(password1),
            hasLetter: /[a-zA-Z]/.test(password1),
            notNumeric: !/^\d+$/.test(password1),
            match: password1.length > 0 && password1 === password2,
        })
    }, [signupData])

    const handleLoginDataChange = (key: string, value: string) => {
        setIsFormActive(true)
        setFormError('')
        setLoginData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const handleSignupDataChange = (key: string, value: string) => {
        setIsFormActive(true)
        setFormError('') // Clear error when user types
        setSignupData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const updateStepInUrl = (step: AuthStep, type?: UserType) => {
        const params = new URLSearchParams()
        params.set('step', step)
        if (type) params.set('type', type)
        setSearchParams(params)
    }

    const isValidEmail = (email: string): boolean => {
        // More robust email validation regex
        const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        return emailRegex.test(email)
    }

    const validateLoginForm = (formData: LoginRequest) => {
        const { email, password } = formData
        
        if (!email || !password) {
            return { isValid: false, message: 'Por favor, completa todos los campos' }
        }
        
        if (!isValidEmail(email)) {
            return { isValid: false, message: 'Por favor, ingresa un correo electrónico válido' }
        }
        
        return { isValid: true, message: '' }
    }

    const validateSignupForm = (formData: SignupRequest) => {
        const { email, password1, password2 } = formData
        
        if (!email || !password1 || !password2) {
            return { isValid: false, message: 'Por favor, completa todos los campos' }
        }
        
        if (!isValidEmail(email)) {
            return { isValid: false, message: 'Por favor, ingresa un correo electrónico válido' }
        }
        
        if (password1 !== password2) {
            return { isValid: false, message: 'Las contraseñas no coinciden' }
        }
        
        if (password1.length < 8) {
            return { isValid: false, message: 'La contraseña debe tener al menos 8 caracteres' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleLoginSubmit = async (e: any) => {
        e.preventDefault()
        setFormError('')

        const validation = validateLoginForm(loginData)
        if (!validation.isValid) {
            setFormError(validation.message)
            toast.error(validation.message)
            return
        }

        try {
            const status: AuthResult = await logIn(loginData, () => console.log('2FA required'))

            if (status === 'success') {
                toast.success(t('success'))
                const defaultRoute = await getDefaultRoute()
                navigate(defaultRoute)
            }

            if (status === 'confirm_email') {
                toast.success(t('login_confirm_email'))
                navigate(routes.verifyEmail)
            }

            return
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.non_field_errors?.[0] || 'Error al iniciar sesión'
            setFormError(errorMessage)
            toast.error(errorMessage)
            console.error('Login error:', error)
        }
    }

    const handleSignupSubmit = async (e: any) => {
        e.preventDefault()
        setFormError('')

        const validation = validateSignupForm(signupData)
        if (!validation.isValid) {
            setFormError(validation.message)
            toast.error(validation.message)
            return
        }

        try {
            console.log('📝 Registering user:', { 
                email: signupData.email.toLowerCase(), 
                user_type: signupData.user_type
            })
            
            const status: boolean = await registerAccount(signupData)
            if (status) {
                toast.success('✅ Registro exitoso. Se envió un código a tu correo.')
                navigate(routes.verifyEmail)
                return
            }
            setFormError('Error en el registro. Por favor, intenta de nuevo.')
            toast.error(t('signup_error'))
        } catch (error: any) {
            console.error('Registration error:', error)
            
            // Parse backend errors
            let errorMessage = 'Error en el registro. Por favor, intenta de nuevo.'
            
            if (error?.response?.data) {
                const errorData = error.response.data
                
                // Check for specific field errors
                if (errorData.email) {
                    errorMessage = Array.isArray(errorData.email) ? errorData.email[0] : errorData.email
                    if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
                        errorMessage = 'Este correo ya está registrado. Intenta iniciar sesión o recuperar tu contraseña.'
                    }
                } else if (errorData.password1) {
                    const pwdError = Array.isArray(errorData.password1) ? errorData.password1[0] : errorData.password1
                    if (pwdError.includes('too short')) {
                        errorMessage = 'La contraseña es demasiado corta. Debe tener al menos 8 caracteres.'
                    } else if (pwdError.includes('too common')) {
                        errorMessage = 'Esta contraseña es demasiado común. Elige una más segura.'
                    } else if (pwdError.includes('numeric')) {
                        errorMessage = 'La contraseña no puede ser completamente numérica.'
                    } else if (pwdError.includes('similar')) {
                        errorMessage = 'La contraseña es muy similar a tu información personal.'
                    } else {
                        errorMessage = pwdError
                    }
                } else if (errorData.password2) {
                    errorMessage = Array.isArray(errorData.password2) ? errorData.password2[0] : errorData.password2
                } else if (errorData.non_field_errors) {
                    errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors
                    if (errorMessage.includes('match')) {
                        errorMessage = 'Las contraseñas no coinciden.'
                    }
                } else if (errorData.message) {
                    errorMessage = errorData.message
                } else if (errorData.detail) {
                    errorMessage = errorData.detail
                }
            }
            
            setFormError(errorMessage)
            toast.error(errorMessage)
        }
    }

    const handleSelectType = (type: UserType) => {
        setSelectedUserType(type)
        setSignupData((prev) => ({ ...prev, user_type: type }))
        setHasBeenSlidDown(true)
        setFormError('')
        setIsFormActive(false)
        
        // Wait for slide-down animation to complete
        setTimeout(() => {
            setCurrentStep('signup')
            updateStepInUrl('signup', type)
        }, 300)
    }
    
    const handleBackToSelectType = () => {
        setCurrentStep('select-type')
        setFormError('')
        setIsFormActive(false)
        setSearchParams({})
    }

    const handleSwitchToLogin = () => {
        setCurrentStep('login')
        setFormError('')
        setIsFormActive(false)
        updateStepInUrl('login')
    }

    const handleSwitchToSignup = () => {
        setCurrentStep('select-type')
        setFormError('')
        setIsFormActive(false)
        setSearchParams({})
    }

    const userTypeLabel = selectedUserType === 'provider' ? 'Domber Amigo' : 'Cliente'

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Back Button */}
                {/* {currentStep !== 'select-type' && (
                    <button 
                        className="auth-back-button"
                        onClick={() => setCurrentStep('select-type')}
                    >
                        <IoChevronBack />
                        <span>Volver</span>
                    </button>
                )} */}

                {/* Welcome Section - Dynamic Based on Step */}
                <div className={`auth-welcome ${isFormActive ? 'auth-welcome--hidden' : ''}`}>
                    <img className="auth-logo-persistent" src="/domber.png" alt="Logo Domber" />
                    
                    {currentStep === 'select-type' && (
                        <div className="auth-welcome-content" key="select-type">
                            <h1 className="auth-welcome-title">Domber</h1>
                            <p className="auth-welcome-tagline">Servicios profesionales a tu alcance</p>
                        </div>
                    )}
                    {currentStep === 'login' && (
                        <div className="auth-welcome-content" key="login">
                            <h1 className="auth-welcome-title auth-form-title">¡Bienvenido de nuevo!</h1>
                            <p className="auth-welcome-tagline">Inicia sesión para continuar</p>
                        </div>
                    )}
                    {currentStep === 'signup' && (
                        <>
                            <div className="auth-welcome-content" key="signup">
                                <h1 className="auth-welcome-title auth-form-title">{selectedUserType == 'provider' ? '¡Ofrece tus servicios!' : '¡Solicita servicios profesionales!'}</h1>
                            </div>
                            <div className="auth-user-type-badge">
                                <span className="badge-label">Registro como</span>
                                <span className="badge-value">{userTypeLabel}</span>
                            </div>
                        </>
                    )}
                </div>

                {/* SELECT TYPE CARD - Always rendered */}
                <div 
                    className={`auth-type-selection ${currentStep !== 'select-type' ? 'slide-down' : ''} ${hasBeenSlidDown ? 'was-slid-down' : ''}`}
                    onClick={() => {
                        // Click to pull card back up when it's down
                        if (currentStep !== 'select-type') {
                            handleBackToSelectType();
                        }
                    }}
                >
                            {/* Type receiver */}
                            <div 
                                className="auth-type-option"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectType('receiver');
                                }}
                            >
                                <div className="auth-type-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="auth-type-title">Pedir Servicios</h3>
                                    <p className="auth-type-desc">Registrate como cliente para solicitar servicios profesionales</p>
                                </div>
                            </div>
                            {/* Type provider */}
                            <div 
                                className="auth-type-option"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectType('provider');
                                }}
                            >
                                <div className="auth-type-icon auth-type-icon--provider">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="auth-type-title">Quiero Trabajar</h3>
                                    <p className="auth-type-desc">Regístrate como Domberamigo para ofrecer servicios profesionales</p>
                                </div>
                            </div>
                            {/* Login button */}
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleSwitchToLogin();
                                }}
                                className="w-full"
                            >
                                Ya tengo cuenta
                            </Button>
                            
                        </div>
                
                {/* Footer - Always at bottom of screen */}
                <div className="auth-footer-global">
                    © 2025 - Domber
                </div>

                {/* LOGIN STEP */}
                {currentStep === 'login' && (
                    <div className="auth-form-container">
                        <div className="auth-card-body">
                            <form className="auth-form" onSubmit={(e) => handleLoginSubmit(e)}>
                                <div>
                                    <Input 
                                        name="email" 
                                        value={loginData.email} 
                                        setValue={(v) => handleLoginDataChange('email', v)} 
                                        label="Email" 
                                        placeholder="tu@email.com"
                                        mode="light"
                                    />
                                    {loginData.email && !isValidEmail(loginData.email) && (
                                        <p className="email-validation-hint">
                                            Por favor, ingresa un correo electrónico válido
                                        </p>
                                    )}
                                </div>
                                <PasswordEyeInput 
                                    name="password" 
                                    value={loginData.password} 
                                    setValue={(v) => handleLoginDataChange('password', v)} 
                                    label="Contraseña" 
                                    placeholder="Tu contraseña"
                                    mode="light"
                                />
                                <Button 
                                    variant="primary" 
                                    size="lg" 
                                    type="submit" 
                                    disabled={isLoading}
                                    className="w-full"
                                >
                                    {isLoading ? <Spinner variant="secondary" size="xs" /> : 'Iniciar Sesión'}
                                </Button>
                            </form>
                            {formError && (
                                <div className="auth-error-message">
                                    {formError}
                                </div>
                            )}
                            <div className="auth-separator">
                                
                            </div>
                            <div className="auth-links">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => navigate(routes.forgotPassword)}
                                    className="w-full"
                                    disabled={isLoading}
                                >
                                    ¿Olvidaste tu contraseña?
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={handleSwitchToSignup}
                                    className="w-full"
                                    disabled={isLoading}
                                >
                                    ¿No tienes cuenta? Regístrate
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* SIGNUP STEP */}
                {currentStep === 'signup' && (
                    <div className="auth-form-container">
                        <div className="auth-card-body">
                            <form className="auth-form" onSubmit={(e) => handleSignupSubmit(e)}>
                                <div>
                                    <Input 
                                        name="email" 
                                        value={signupData.email} 
                                        setValue={(v) => handleSignupDataChange('email', v)} 
                                        label="Email" 
                                        placeholder="tu@email.com"
                                        mode="light"
                                    />
                                    {signupData.email && !isValidEmail(signupData.email) && (
                                        <p className="email-validation-hint">
                                            Por favor, ingresa un correo electrónico válido
                                        </p>
                                    )}
                                </div>
                                <PasswordEyeInput 
                                    name="password1" 
                                    value={signupData.password1} 
                                    setValue={(v) => handleSignupDataChange('password1', v)} 
                                    label="Contraseña" 
                                    placeholder="Tu contraseña"
                                    mode="light"
                                />
                                <PasswordEyeInput 
                                    name="password2" 
                                    value={signupData.password2} 
                                    setValue={(v) => handleSignupDataChange('password2', v)} 
                                    label="Confirmar Contraseña" 
                                    placeholder="Confirma tu contraseña"
                                    mode="light"
                                />
                                
                                {/* Password Requirements */}
                                {signupData.password1 && !(
                                    passwordRequirements.minLength && 
                                    passwordRequirements.hasLetter && 
                                    passwordRequirements.notNumeric && 
                                    passwordRequirements.match
                                ) && (
                                    <div className="password-requirements">
                                        <p className="password-requirements-title">Tu contraseña debe tener:</p>
                                        <div className="password-requirements-list">
                                            <div className={`password-requirement ${passwordRequirements.minLength ? 'valid' : ''}`}>
                                                <span className="requirement-icon">{passwordRequirements.minLength ? '✓' : '○'}</span>
                                                <span className="requirement-text">Al menos 8 caracteres</span>
                                            </div>
                                            <div className={`password-requirement ${passwordRequirements.hasLetter ? 'valid' : ''}`}>
                                                <span className="requirement-icon">{passwordRequirements.hasLetter ? '✓' : '○'}</span>
                                                <span className="requirement-text">Al menos una letra</span>
                                            </div>
                                            <div className={`password-requirement ${passwordRequirements.notNumeric ? 'valid' : ''}`}>
                                                <span className="requirement-icon">{passwordRequirements.notNumeric ? '✓' : '○'}</span>
                                                <span className="requirement-text">No debe incluir solo números</span>
                                            </div>
                                            {signupData.password2 && (
                                                <div className={`password-requirement ${passwordRequirements.match ? 'valid' : ''}`}>
                                                    <span className="requirement-icon">{passwordRequirements.match ? '✓' : '○'}</span>
                                                    <span className="requirement-text">Las contraseñas coinciden</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                <Button 
                                    variant="primary" 
                                    size="lg" 
                                    type="submit" 
                                    disabled={isLoading}
                                    className="w-full"
                                >
                                    {isLoading ? <Spinner variant="secondary" size="xs" /> : 'Registrarse'}
                                </Button>
                            </form>
                            {formError && (
                                <div className="auth-error-message">
                                    {formError}
                                </div>
                            )}
                            <div className="auth-separator">
                                
                            </div>
                            <div className="auth-links">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={handleSwitchToLogin}
                                    className="w-full"
                                    disabled={isLoading}
                                >
                                    ¿Ya tienes cuenta? Inicia sesión
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Auth
